import { useCallback, useState, useEffect } from 'react';
import Drawer from 'common/components/Drawer/Drawer';
import { Event, ModifiedEvent } from 'types/Event';
import Button from 'common/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { MeetingStatusSetByMediator } from 'types/Meeting';
import fetchJSON from 'common/utils/fetchJSON';
import Modal from 'common/components/Modal/Modal';
import EventForm from './EventForm';
import EventDetails from './EventDetails';

export interface EventDrawerProps {
  event: Event | null;
  setEvent: React.Dispatch<React.SetStateAction<Event | null>>;
  setSelectedEvent: React.Dispatch<React.SetStateAction<ModifiedEvent | null>>;
  onSubmit: (values: any) => Promise<any>;
  newEventType?: 'availability' | 'personal';
  onEventChanged?: () => Promise<any>;
  onDocumentAdded?: (documentId: number) => Promise<void>;
  onDocumentDeleted?: (documentId: number) => Promise<void>;
  onDeleteEvent?: (recurrenceEditMode: 'all' | 'future' | 'single' | null) => Promise<void>;
}

/**
 * Composant de drawer unifié pour afficher les détails d'un événement et permettre sa modification
 */
function EventDrawer({
  event,
  setEvent,
  setSelectedEvent,
  onSubmit,
  newEventType = 'personal',
  onEventChanged,
  onDocumentAdded,
  onDocumentDeleted,
  onDeleteEvent,
}: EventDrawerProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [recurrenceEditMode, setRecurrenceEditMode] = useState<'all' | 'future' | 'single' | null>(
    null
  );

  // When event changes, update drawer state and current event
  useEffect(() => {
    if (event) {
      setCurrentEvent(event);
      setIsDrawerOpen(true);
    }
  }, [event]);

  const handleCloseDrawer = useCallback(() => {
    // First close the drawer
    setIsDrawerOpen(false);
    setSelectedEvent(null);

    // Then clear the event data after animation completes
    setTimeout(() => {
      setEvent(null);
      setIsEditing(false);
    }, 300); // Match with CSS transition duration
  }, [setEvent, setSelectedEvent]);

  const handleStartEdit = () => {
    // Si l'évènement est récurrent (isRecurringInstance === true),
    // il faut demander à l'utilisateur s'il souhaite modifier :
    //  - tous les évènements récurrents
    //  - tous les évènements récurrents à partir de cette date
    //  - cette seule instance
    if (currentEvent?.isRecurringInstance) {
      setShowRecurrenceModal(true);
    } else {
      setIsEditing(true);
    }
  };

  const handleDeleteEvent = useCallback(
    async (recurrenceEditModeForDeletion: 'all' | 'future' | 'single' | null) => {
      await onDeleteEvent?.(recurrenceEditModeForDeletion);
      handleCloseDrawer();
    },
    [onDeleteEvent, handleCloseDrawer]
  );

  const handleStartDelete = useCallback(() => {
    // Si l'évènement est récurrent (isRecurringInstance === true),
    // il faut demander à l'utilisateur s'il souhaite modifier :
    //  - tous les évènements récurrents
    //  - tous les évènements récurrents à partir de cette date
    //  - cette seule instance
    if (currentEvent?.isRecurringInstance) {
      setIsDeleting(true);
      setShowRecurrenceModal(true);
    } else {
      handleDeleteEvent(null);
    }
  }, [currentEvent, handleDeleteEvent, setIsDeleting, setShowRecurrenceModal]);

  const handleRecurrenceChoice = useCallback(
    (choice: 'all' | 'future' | 'single') => {
      setRecurrenceEditMode(choice);
      setShowRecurrenceModal(false);
      if (isDeleting) {
        handleDeleteEvent(choice);
      } else {
        setIsEditing(true);
      }
      onEventChanged?.();
    },
    [isDeleting, onEventChanged, handleDeleteEvent]
  );

  const handleCancelEdit = () => {
    if (currentEvent?.id) {
      setIsEditing(false);
      setRecurrenceEditMode(null);
    } else {
      setIsDrawerOpen(false);
      setTimeout(() => {
        setEvent(null);
        setSelectedEvent(null);
      }, 300);
    }
  };

  const handleSubmit = async (values: any) => {
    // Add recurrence edit mode to values if needed
    const submitValues = recurrenceEditMode ? { ...values, recurrenceEditMode } : values;

    const result = await onSubmit(submitValues);
    if (!result?.message) {
      setIsEditing(false);
      setRecurrenceEditMode(null);
      onEventChanged?.();
    }
    return result;
  };

  const changeMeetingStatus = useCallback(
    async (status: MeetingStatusSetByMediator) => {
      let action;
      switch (status) {
        case 'accepted':
          action = 'accept';
          break;
        case 'denied':
          action = 'refuse';
          break;
        case 'rescheduled_by_mediator':
          action = 'askForReschedule';
          break;
        case 'cancelled':
          action = 'cancel';
          break;
        default:
          throw new Error('Invalid status');
      }
      try {
        if (currentEvent?.meeting) {
          const res = await fetchJSON({
            method: 'GET',
            url: `meetings/data/${currentEvent.meeting.id}/${action}`,
          });
          if (res) {
            onEventChanged?.();
            if (status !== 'accepted') {
              handleCloseDrawer();
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [currentEvent?.meeting, handleCloseDrawer, onEventChanged]
  );

  return (
    <>
      <Modal
        visible={showRecurrenceModal}
        onCloseModal={() => setShowRecurrenceModal(false)}
        title={
          isDeleting ? t('agenda.recurrenceModalDeleteTitle') : t('agenda.recurrenceModalEditTitle')
        }
      >
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col items-center gap-3 mt-2">
            <Button
              className="w-full max-w-xs py-2 px-4 bg-primary text-white text-left rounded-md hover:bg-primary-dark"
              onClick={() => handleRecurrenceChoice('single')}
              title={t('agenda.recurrenceSingle')}
            />
            <Button
              className="w-full max-w-xs py-2 px-4 bg-primary text-white text-left rounded-md hover:bg-primary-dark"
              onClick={() => handleRecurrenceChoice('future')}
              title={t('agenda.recurrenceFuture')}
            />
            <Button
              className="w-full max-w-xs py-2 px-4 bg-primary text-white text-left rounded-md hover:bg-primary-dark"
              onClick={() => handleRecurrenceChoice('all')}
              title={t('agenda.recurrenceAll')}
            />
          </div>
        </div>
      </Modal>

      <Drawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} anchor="right">
        {isEditing || !currentEvent?.id ? (
          <EventForm
            event={currentEvent}
            recurrenceEditMode={recurrenceEditMode}
            newEventType={newEventType}
            onChangeEvent={setSelectedEvent}
            onSubmit={handleSubmit}
            onCancel={handleCancelEdit}
          />
        ) : (
          <div>
            <EventDetails
              event={currentEvent}
              onDocumentAdded={onDocumentAdded}
              onDocumentDeleted={onDocumentDeleted}
              onChangeMeetingStatus={changeMeetingStatus}
            />

            {currentEvent?.eventType !== 'meeting' && !currentEvent?.isExternalEvent && (
              <div className="p-4 mt-4 flex flex-col items-end gap-2">
                <Button
                  className="border border-secondary text-fourth hover:bg-secondary hover:text-white"
                  onClick={handleStartEdit}
                  icon="PencilIcon"
                  title={t('actions.edit')}
                />
                <Button
                  className="border border-danger text-danger hover:bg-danger hover:text-white"
                  onClick={handleStartDelete}
                  icon="TrashIcon"
                  title={t('actions.delete')}
                  confirmMessage={
                    !currentEvent?.isRecurringInstance ? t('actions.confirmDelete') : undefined
                  }
                />
              </div>
            )}
            {currentEvent?.isExternalEvent && (
              <div className="p-4 mt-4 flex flex-col items-end gap-2 w-full">
                <p className="text-center w-full text-gray-500 text-xs">
                  {t('agenda.externalEventModificationForbidden')}
                </p>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}

export default EventDrawer;
